/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Collapse, Button } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { getWatermarksForPicker } from '../../../services/api/watermarks'
import { useRcmTableData } from '../../../hooks/useRcmTableData'

const persistenceKey = 'settingsWatermarksFilter'

interface FilterFormValues {
  types: any[]
}

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const getDefaultFilter = (): FilterFormValues => ({
    types: []
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(getDefaultFilter())

  useEffect(() => {
    // Load persisted filter on mount
    const persistedFilter = context.service.persist.getRouteScoped<FilterFormValues>(persistenceKey)
    if (persistedFilter && persistedFilter.types && persistedFilter.types.length > 0) {
      setFormValues(persistedFilter)
      tableData.applyFilter(persistedFilter)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setRouteScoped(persistenceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const isFilterDefault = (filter: FilterFormValues = formValues) => {
    const defaultFilter = getDefaultFilter()
    return JSON.stringify(filter) === JSON.stringify(defaultFilter)
  }

  const updateField = (field: keyof FilterFormValues, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-xs-4">
              <div className="form-group" id="1bddb1d6-8615-4c98-8f4e-05bc1904d9c1">
                <label className="control-label">{translate('WATERMARKS_TITLE')}</label>
                <InfiniteScrollSelect
                  placeholder={translate('SELECT_WATERMARKS...')}
                  name="types"
                  value={formValues.types}
                  asyncLoadFn={async (search, page) => {
                    try {
                      return await getWatermarksForPicker(search, page)
                    } catch (error) {
                      console.error('Error loading watermarks for filter:', error)
                      return {
                        options: [],
                        hasMore: false
                      }
                    }
                  }}
                  onChange={(option) => {
                    updateField('types', option ? option : [])
                  }}
                  multi={true}
                  searchable
                  id="1bddb1d6-8615-4c98-8f4e-05bc1904d9c1"
                />
              </div>
            </div>
          </div>
          <div>
            <button
              data-action="filter"
              className="btn btn-primary"
              onClick={handleApplyFilter}
              id="cb6c35e1-becf-49ad-b30b-92ac9f910c30"
            >
              {translate('BTN_FILTER')}
            </button>
            <button
              data-action="reset"
              disabled={isFilterDefault()}
              className="btn btn-chita btn-link"
              onClick={handleClearFilter}
              id="0cf99103-6214-4489-b196-602fe766b7f4"
            >
              {translate('BTN_CLEAR_FILTER')}
            </button>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
